import { add, DateString, Days, Euros, Factor, Hours, multiplyWithFactor, subtract, sum } from "@pentacode/openapi";
import { dateAdd, toDateString, getRange } from "../utils";
import { getPayrollIssues, getShiftIssues } from "../issues";
import { Balances, Company, Employee, TimeEntry, TimeEntryType, VacationIncrement } from "../model";
import { calcVacationEntitlement } from "../vacation";
import { getPayrollPeriod } from "../payroll";
import { DateRange } from "./date-range";
import { getDailyAverage, getTimeResult, getTotalResults, makeEmptyTimeResult, TimeResult } from "./time-result";
import { createAbsenceHoursGetter, getNominalBonus, getNominalTime } from "./nominals";
import { roundBalances } from "./balances";
import { DailyResultsDTO } from "../api/rpc";

export function getDailyResults(
    company: Company,
    employee: Employee,
    entries: TimeEntry[],
    { from, to }: DateRange,
    previousResults: DailyResultsDTO[]
): DailyResultsDTO[] {
    // !!! This Function also potentially mutates the input TimeEntries and updates their results if not set

    const dailyResults: DailyResultsDTO[] = [];
    let date = from;
    let lastResult = previousResults[previousResults.length - 1];

    // time results are not used in this function, so we can call it before results are updated
    const issues = getShiftIssues(employee, company, entries, { from, to });

    while (date < to) {
        const dayEntries = entries.filter((e) => e.date === date);
        const workEntries = dayEntries.filter((e) => e.type === TimeEntryType.Work);
        const reset = dayEntries.find((e) => e.type === TimeEntryType.ResetLedgers);
        const allResults = [...previousResults, ...dailyResults];

        const lastDayOfPreviousMonth = dateAdd(getRange(date, "month").from, { days: -1 });
        const averagesResult = allResults.find((res) => res.date === lastDayOfPreviousMonth);

        const contract = employee.getContractForDate(date);

        const results: DailyResultsDTO["results"] = {
            work: makeEmptyTimeResult(),
            workPlanned: makeEmptyTimeResult(),
            absences: makeEmptyTimeResult(),
            adjustments: makeEmptyTimeResult(),
            total: makeEmptyTimeResult(),
        };

        let vacationDays = 0 as Days;
        let vacationAdjustments = 0 as Days;

        if (contract) {
            for (const entry of dayEntries) {
                if (!entry.result) {
                    entry.result = getTimeResult(
                        company,
                        employee,
                        entry,
                        averagesResult?.averages["13weeks"] || makeEmptyTimeResult(),
                        workEntries.filter((e) => e.startFinal && e.endFinal).length,
                        "final"
                    );
                }
                if (!entry.resultPlanned) {
                    entry.resultPlanned = getTimeResult(
                        company,
                        employee,
                        entry,
                        averagesResult?.averages["13weeks"] || makeEmptyTimeResult(),
                        workEntries.length,
                        "planned"
                    );
                }
                if (!entry.resultMaxTotalCosts) {
                    const { totalCosts } = getTimeResult(
                        company,
                        employee,
                        entry,
                        averagesResult?.averages["13weeks"] || makeEmptyTimeResult(),
                        workEntries.length,
                        "max"
                    );
                    entry.resultMaxTotalCosts = totalCosts;
                }
            }

            vacationDays = dayEntries
                .filter((e) => e.type === TimeEntryType.Vacation)
                .reduce((total, e) => add(total, e.days ?? (1 as Days)), 0 as Days);

            vacationAdjustments = dayEntries
                .filter((e) => e.type === TimeEntryType.VacationAdjustment)
                .reduce((total, e) => add(total, e.days ?? (1 as Days)), 0 as Days);

            results.work = getTotalResults(workEntries.map((e) => e.result).filter(Boolean) as TimeResult[]);
            results.workPlanned = getTotalResults(
                workEntries.map((e) => e.resultPlanned).filter(Boolean) as TimeResult[]
            );

            results.absences = getTotalResults(
                dayEntries
                    .filter((e) =>
                        [
                            TimeEntryType.Sick,
                            TimeEntryType.Vacation,
                            TimeEntryType.Sick,
                            TimeEntryType.SickInKUG,
                            TimeEntryType.ChildSick,
                            TimeEntryType.CompDay,
                        ].includes(e.type)
                    )
                    .map((e) => e.result)
                    .filter(Boolean) as TimeResult[]
            );

            results.adjustments = getTotalResults(
                dayEntries
                    .filter((e) => [TimeEntryType.HourAdjustment].includes(e.type))
                    .map((e) => e.result)
                    .filter(Boolean) as TimeResult[]
            );

            results.total = getTotalResults([results.work, results.absences, results.adjustments]);
        }

        const getTimeBalance = (
            _13weekAverage: Pick<TimeResult, "base" | "bonuses" | "breaks">,
            planned: boolean
        ): DailyResultsDTO["timeBalance"] => {
            const carry =
                (planned ? lastResult?.timeBalancePlanned?.balance : lastResult?.timeBalance.balance) || (0 as Hours);
            const nominal = getNominalTime(
                company,
                employee,
                { from: date, to: dateAdd(date, { days: 1 }) },
                //we go day by day here, so we can use the last 13 weeks average
                createAbsenceHoursGetter([{ date, averages: { "13weeks": _13weekAverage } }])
            );
            const work = planned ? results.workPlanned?.base.duration || (0 as Hours) : results.work.base.duration;
            const absences = results.absences.base.duration;
            const adjustments = results.adjustments.base.duration;
            const actual = sum(work, absences, adjustments);
            const difference = subtract(actual, nominal);
            const base = reset?.hours ?? carry;
            const balance = add(base, difference);

            return {
                reset: reset?.hours ?? undefined,
                nominal,
                work,
                adjustments,
                absences,
                actual,
                difference,
                carry,
                balance,
            };
        };

        const getVacationBalance: (
            timeBalance: DailyResultsDTO["timeBalance"]
        ) => DailyResultsDTO["vacationBalance"] = (timeBalance) => {
            const contract = employee.getContractForDate(date);
            const carry = lastResult?.vacationBalance.balance || (0 as Days);
            const base = reset?.days ?? carry;
            const vacationFactor = (
                contract?.vacationIncrement === VacationIncrement.Hourly && timeBalance.nominal
                    ? timeBalance.actual / timeBalance.nominal
                    : 1
            ) as Factor;
            let nominal = calcVacationEntitlement(
                employee,
                { from: date, to: dateAdd(date, { days: 1 }) },
                company.settings
            );
            nominal = multiplyWithFactor(nominal, vacationFactor);
            const actual = add(vacationDays, vacationAdjustments);
            const difference = subtract(nominal, actual);
            const balance = add(base, difference);

            return {
                reset: reset?.days ?? undefined,
                nominal,
                taken: vacationDays,
                adjustments: vacationAdjustments,
                actual,
                difference,
                carry,
                balance,
            };
        };

        const getBonusesBalance: () => DailyResultsDTO["bonusesBalance"] = () => {
            const taxed = results.total.bonuses
                .filter((b) => !b.taxFree)
                .reduce((total, b) => add(total, b.wages), 0 as Euros);
            const untaxed = results.total.bonuses
                .filter((b) => b.taxFree)
                .reduce((total, b) => add(total, b.wages), 0 as Euros);
            const actual = company.settings.includeTaxedBonusesInBalance ? add(taxed, untaxed) : untaxed;

            if (!contract?.enableSFNLedger) {
                return {
                    taxed,
                    untaxed,
                    actual,
                };
            }

            const nominal = getNominalBonus(employee, { from: date, to: dateAdd(date, { days: 1 }) });
            const difference = subtract(actual, nominal);

            // Bonuses balance is always reset with the beginning of a new year
            const carry = date.endsWith("01-01") ? (0 as Euros) : lastResult?.bonusesBalance.balance || (0 as Euros);
            const base = reset?.resetBonus ?? carry;
            const balance = add(base, difference);

            return {
                reset: reset?.resetBonus ?? undefined,
                nominal,
                taxed,
                untaxed,
                actual,
                difference,
                carry,
                balance,
            };
        };

        let _13weekAverage = lastResult?.averages["13weeks"] || makeEmptyTimeResult();
        // For historical reasons, we calculate a new 13 week average for each month
        // and use the same average for the whole month instead of calculating a new
        // (more accurate) value for each day
        if (dateAdd(date, { days: 1 }).endsWith("-01")) {
            // The previous daily results act as the basis for calcuating the average
            const allResults = [...previousResults, ...dailyResults];
            // We start calculating from the last work day
            const lastWorkDate = results.work.base.duration
                ? date
                : [...allResults].reverse().find((r) => r.results.work.days)?.date || date;
            const to = dateAdd(lastWorkDate, { days: 1 });
            // Averages are calculated from the last 13 weeks
            // (calculating backwards from the last work day)
            const from = dateAdd(to, { weeks: -13 });
            const averagesInput = allResults.filter((r) => r.date >= from && r.date < to);
            // Only work results are considered
            const workResults = [...averagesInput.map((e) => e.results.work), results.work];
            _13weekAverage = getDailyAverage(workResults);
        }

        const timeBalance = getTimeBalance(_13weekAverage, false);

        const result = new DailyResultsDTO({
            employeeId: employee.id,
            date,
            averages: {
                "13weeks": _13weekAverage,
            },
            results,
            timeBalance,
            timeBalancePlanned: getTimeBalance(_13weekAverage, true),
            vacationBalance: getVacationBalance(timeBalance),
            bonusesBalance: getBonusesBalance(),
            issues: issues.filter((i) => i.date === date),
        });

        // If this is the last date of the payroll period, check for payroll issues
        if (new Date(dateAdd(date, { days: 1 })).getDate() === company.settings.startPayrollPeriod) {
            result.issues!.push(
                ...getPayrollIssues(
                    company,
                    employee,
                    entries,
                    getPayrollPeriod(date, company.settings.startPayrollPeriod)
                )
            );
        }

        dailyResults.push(result);
        lastResult = result;

        date = dateAdd(date, { days: 1 });
    }

    return dailyResults;
}

/** @deprecated Use `timeBalanceFromDailyResults`, `vacationBalanceFromDailyResults`, `bonusesBalanceFromDailyResults` instead */
export function aggregateDailyResults(
    results: DailyResultsDTO[],
    roundValues = true,
    projected = false
): Omit<Balances, "employeeId" | "from" | "to"> {
    const firstResult = results[0];
    const today = toDateString(new Date());
    const firstTimeBalance =
        (projected && firstResult?.date >= today && firstResult?.timeBalancePlanned) || firstResult?.timeBalance;

    const balances = results.reduce(
        (total, result) => {
            const timeBalance = (projected && result.date >= today && result.timeBalancePlanned) || result.timeBalance;
            return {
                ...total,
                time: {
                    ...total.time,
                    carry: total.time.carry ?? timeBalance.carry,
                    actual: add(total.time.actual, timeBalance.actual),
                    nominal: add(total.time.nominal, timeBalance.nominal),
                    difference: add(total.time.difference, timeBalance.difference),
                    work: add(total.time.work, timeBalance.work),
                    absences: add(total.time.absences, timeBalance.absences),
                    adjustments: add(total.time.adjustments, timeBalance.adjustments),
                    balance: timeBalance.balance ?? total.time.balance,
                },
                vacation: {
                    ...total.vacation,
                    carry: total.vacation.carry ?? result.vacationBalance.carry,
                    taken: add(total.vacation.taken, result.vacationBalance.taken),
                    adjustments: add(total.vacation.adjustments, result.vacationBalance.adjustments),
                    actual: add(total.vacation.actual, result.vacationBalance.actual),
                    nominal: add(total.vacation.nominal, result.vacationBalance.nominal),
                    difference: add(total.vacation.difference, result.vacationBalance.difference),
                    balance: result.vacationBalance.balance ?? total.vacation.balance,
                },
                bonuses: {
                    ...total.bonuses,
                    carry: total.bonuses.carry ?? result.bonusesBalance.carry,
                    taxed: add(total.bonuses.taxed, result.bonusesBalance.taxed),
                    untaxed: add(total.bonuses.untaxed, result.bonusesBalance.untaxed),
                    actual: add(total.bonuses.actual, result.bonusesBalance.actual),
                    nominal:
                        typeof total.bonuses.nominal === "number" && typeof result.bonusesBalance.nominal === "number"
                            ? add(total.bonuses.nominal, result.bonusesBalance.nominal)
                            : (total.bonuses.nominal ?? result.bonusesBalance.nominal),
                    difference:
                        typeof total.bonuses.difference === "number" &&
                        typeof result.bonusesBalance.difference === "number"
                            ? add(total.bonuses.difference, result.bonusesBalance.difference)
                            : (total.bonuses.difference ?? result.bonusesBalance.difference),
                    balance: result.bonusesBalance.balance ?? total.bonuses.balance,
                },
            };
        },
        {
            time: {
                carry: firstTimeBalance?.carry || (0 as Hours),
                work: 0 as Hours,
                absences: 0 as Hours,
                adjustments: 0 as Hours,
                actual: 0 as Hours,
                nominal: 0 as Hours,
                difference: 0 as Hours,
                balance: firstTimeBalance?.balance || (0 as Hours),
                reset: firstTimeBalance?.reset,
            },
            vacation: {
                carry: firstResult?.vacationBalance.carry || (0 as Days),
                adjustments: 0 as Days,
                taken: 0 as Days,
                actual: 0 as Days,
                nominal: 0 as Days,
                difference: 0 as Days,
                balance: firstResult?.vacationBalance.balance || (0 as Days),
                reset: firstResult?.vacationBalance.reset,
            },
            bonuses: {
                carry: firstResult?.bonusesBalance.carry,
                taxed: 0 as Euros,
                untaxed: 0 as Euros,
                actual: 0 as Euros,
                balance: firstResult?.bonusesBalance.balance,
                reset: firstResult?.bonusesBalance.reset,
            },
        } as Omit<Balances, "employeeId" | "from" | "to">
    );

    return roundValues ? roundBalances(balances) : balances;
}
